import fetch from 'isomorphic-unfetch';
import Head from 'next/head';
import { useRouter } from 'next/router';
import DefaultErrorPage from 'next/error';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { useTranslation } from 'next-i18next';
import { getStrippedEntityId } from 'lib/entityIds';
import Layout from 'components/Layout';
import FlashMessage from 'components/FlashMessage';
import CompanyLogo from 'components/CompanyLogo';
import Company from 'svg/icon-company.svg';
import Location from 'svg/icon-pin.svg';
import Properties from 'components/Properties';
import DoubleChevronLeft from 'svg/double-chevron-left.svg';
import { useVacancy } from 'utils';
import Button from 'components/Button';
import CombinedForm from 'components/CombinedForm';
import hasAuthToken from 'lib/hasAuthToken';

const Applicationpage = () => {
  const { t } = useTranslation();
  // Get the vacancy id from the url
  const router = useRouter();
  const { id: vacancyId, referrer } = router.query;
  // Retrieve the vacancy information
  const { vacancy, loadingVacancy, vacancyError, userHasAlreadyApplied } =
    useVacancy({
      vacancyId,
    });

  // Add referrer as base url if available
  const getBackToVacancyUrl = () =>
    `${referrer || ''}/vacature/${
      vacancy.slug
    }-${vacancy.address.city?.toLowerCase()}/${getStrippedEntityId(
      vacancy.id
    )}`;

  if (!loadingVacancy && vacancyError)
    return <DefaultErrorPage statusCode={404} />;

  return (
    <>
      <Head>
        <link
          href="https://fonts.googleapis.com/css2?family=Open+Sans:wght@400;600;700"
          rel="stylesheet"
        />
        <link
          href="https://fonts.googleapis.com/css2?family=Roboto:wght@400;500;700"
          rel="stylesheet"
        />
        <link
          href="https://fonts.googleapis.com/css2?family=Roboto+Slab:wght@400;600;700"
          rel="stylesheet"
        />
        <link
          href="https://fonts.googleapis.com/css2?family=Merriweather:wght@400;700"
          rel="stylesheet"
        />
        {vacancy && <link rel="canonical" href={getBackToVacancyUrl()} />}
        <meta name="robots" content="noindex, follow" />
      </Head>
      <Layout title={t('applicationPage.pageTitle')} noHeader noFooter>
        <div className="o-retain o-retain--wall">
          <div className="c-combined-form">
            {!loadingVacancy && vacancy && (
              <>
                <CompanyLogo
                  company={{
                    logo: vacancy.logo || vacancy.companyBranch.logo,
                    company: vacancy.companyBranch.company,
                  }}
                />
                <h1 className="u-margin-bottom-small u-margin-top-small">
                  {vacancy.title}
                </h1>
                <div className="o-layout o-layout--gutter-base o-layout--align-middle o-layout--justify-space-between">
                  <div className="o-layout__cell o-layout__cell--fit">
                    <Properties
                      properties={[
                        {
                          Icon: Company,
                          label: t('property.company'),
                          value: vacancy.companyBranch.company,
                        },
                        {
                          Icon: Location,
                          label: t('property.location'),
                          value: vacancy.address.city,
                        },
                      ]}
                    />
                  </div>
                  <div className="o-layout__cell o-layout__cell--fit">
                    <Button
                      Tag="a"
                      href={getBackToVacancyUrl()}
                      label={t('back_to_vacancy.label')}
                      IconLeft={DoubleChevronLeft}
                      extraClasses="c-button--small c-button--link c-button--chevron"
                    />
                  </div>
                </div>
                {userHasAlreadyApplied && (
                  <FlashMessage
                    test="userHasAlreadyApplied"
                    extraClasses="u-margin-top-base"
                    message={t('applicationPage.alreadyApplied')}
                    type="success"
                  />
                )}
                <CombinedForm
                  translationKey="applicationPage"
                  shouldHideForm={userHasAlreadyApplied !== false}
                  vacancy={vacancy}
                />
              </>
            )}
          </div>
        </div>
      </Layout>
    </>
  );
};

export async function getServerSideProps({ req, res, locale, query }) {
  if (!hasAuthToken(req)) {
    res.setHeader(
      'Cache-Control',
      'public, max-age=300, s-maxage=300, stale-while-revalidate=600'
    );
  }

  const { referrer } = query;
  let websiteSettings = {};

  if (referrer) {
    // Retrieve the subdomain from the url
    const slug = referrer
      .split('.')[0]
      .replace('https://', '')
      .replace('-debanensite', '');
    // Fetch the website settings
    websiteSettings = await fetch(
      `${process.env.API_ENDPOINT}/company_websites/s/${slug}`
    )
      .then(response => response.json())
      .catch(e => console.log(e));
  }

  return {
    props: {
      websiteSettings,
      ...(await serverSideTranslations(locale, ['common'])),
    },
  };
}

export default Applicationpage;
